import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { formatNzDate, nzDateTimeParts } from '@promanage/core';
import { Button, Chip } from '@promanage/ui';
import { DataTable } from '../features/table/DataTable';
import { EmptyState, FilterChips, ListShell } from '../features/table/ListShell';
import { compareRows, type ColumnDef, type SortState } from '../features/table/columns';
import { CommunicationsNav } from '../features/lists/CommunicationsNav';
import { useUnreadMail } from '../features/inbox/UnreadMail';
import { trpc, type RouterOutputs } from '../lib/trpc';

type Message = RouterOutputs['mailInbox']['list']['messages'][number];
type Column = 'from' | 'message' | 'assigned' | 'received';
type Filter = 'mine' | 'unassigned' | 'unread' | 'all' | 'deleted';

/** One view of the inbox, a page at a time — the view and the search are the server's. */
const inboxOptions = (filter: Filter, search: string) =>
  trpc.mailInbox.list.infiniteQueryOptions(
    { filter, search },
    {
      staleTime: 10_000,
      getNextPageParam: (page) => page.nextCursor,
      // The rows on screen stay while the next view or search is fetched, rather than blinking out.
      placeholderData: keepPreviousData,
    },
  );
const countsOptions = () => trpc.mailInbox.counts.queryOptions(undefined, { staleTime: 10_000 });

export const Route = createFileRoute('/communications/inbox/')({
  // The page opens on Mine; warm that and the chips' numbers.
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.prefetchInfiniteQuery(inboxOptions('mine', '')),
      context.queryClient.prefetchQuery(countsOptions()),
    ]),
  component: InboxPage,
});

/** The value `delay` ms after it last changed — so the search runs when typing pauses. */
function useDebounced<T>(value: T, delay: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return settled;
}

const when = (at: string) => {
  const parts = nzDateTimeParts(new Date(at));
  return `${formatNzDate(parts.date)} ${parts.time}`;
};

const COLUMNS: readonly ColumnDef<Column, Message>[] = [
  {
    id: 'from',
    label: 'From',
    width: '15rem',
    fixed: true,
    defaultVisible: true,
    sortValue: (message) =>
      (message.personName ?? message.fromName ?? message.fromAddress).toLowerCase(),
    render: (message) => (
      <div className="flex min-w-0 items-start gap-2">
        {/* The dot is the unread mark; the weight below repeats it for anybody who cannot see it. */}
        <span
          aria-hidden="true"
          className={`mt-1.5 size-2 shrink-0 rounded-full ${message.unread ? 'bg-accent' : 'bg-transparent'}`}
        />
        <div className="min-w-0">
          <span className={`block truncate ${message.unread ? 'font-semibold' : ''}`}>
            {message.unread ? <span className="sr-only">Unread. </span> : null}
            {message.personName ?? message.fromName ?? message.fromAddress}
          </span>
          {/*
           * WHO IT CLAIMS TO BE FROM, AND WHETHER THAT HELD UP. A From address is whatever the
           * sender typed, so a message that failed the sender's own domain policy says so here —
           * before anybody acts on "please send my rent refund to this new account".
           */}
          <span
            className={`block truncate text-[0.75rem] ${message.senderVerified ? 'text-ink-subtle' : 'text-warn'}`}
          >
            {message.senderVerified ? message.fromAddress : `${message.fromAddress} — not verified`}
          </span>
        </div>
      </div>
    ),
  },
  {
    id: 'message',
    label: 'Message',
    width: '26rem',
    defaultVisible: true,
    sortValue: (message) => (message.subject ?? '').toLowerCase(),
    render: (message) => (
      <div className="min-w-0">
        <Link
          to="/communications/inbox/$id"
          params={{ id: message.id }}
          className={`block truncate hover:text-accent hover:underline ${message.unread ? 'font-semibold' : ''}`}
        >
          {message.subject ?? '(no subject)'}
        </Link>
        <span className="block truncate text-[0.75rem] text-ink-subtle">
          {message.preview ?? 'No text'}
          {message.attachmentCount > 0
            ? ` · ${String(message.attachmentCount)} ${message.attachmentCount === 1 ? 'attachment' : 'attachments'}`
            : ''}
        </span>
      </div>
    ),
  },
  {
    id: 'assigned',
    label: 'Assigned to',
    width: '11rem',
    defaultVisible: true,
    sortValue: (message) => message.assignedUserName,
    render: (message) =>
      message.assignedUserName ? (
        <span className="block truncate">{message.assignedUserName}</span>
      ) : (
        <Chip tone="warn">Unassigned</Chip>
      ),
  },
  {
    id: 'received',
    label: 'Received',
    width: '9rem',
    defaultVisible: true,
    sortValue: (message) => message.receivedAt,
    render: (message) => <span className="tabular-nums">{when(message.receivedAt)}</span>,
  },
];

const NOTHING: Record<Filter, { title: string; body: string }> = {
  mine: {
    title: 'Nothing assigned to you',
    body: 'Mail from the people on your properties, and replies to what you send, arrives here.',
  },
  unassigned: {
    title: 'Nothing unassigned',
    body: 'A message nobody could be matched to waits here until somebody takes it.',
  },
  unread: { title: 'Nothing unread', body: 'You have opened everything you can see.' },
  deleted: {
    title: 'Nothing deleted',
    body: 'A deleted email waits here and can be restored. Its attachments are removed for good a year after it is deleted.',
  },
  all: {
    title: 'No mail yet',
    body: 'Messages sent to your agency’s mail address arrive here. Claim the address under Settings › Delivery.',
  },
};

/**
 * The agency's shared inbox — every message sent to its mail address, newest first.
 *
 * ── ONE INBOX; "MINE" IS A FILTER ────────────────────────────────────────────────────────────
 *
 * There are no private mailboxes. Each message belongs to the agency and is assigned to at most
 * one person — by the reply token on what it answers, failing that by who manages the sender's
 * property, failing that by nobody, and then it waits under Unassigned for somebody to take it.
 * The page opens on Mine because that is the work; the other chips are the same list seen wider.
 *
 * ── UNREAD IS YOURS ──────────────────────────────────────────────────────────────────────────
 *
 * The dot means YOU have not opened it. An administrator reading across the office does not make a
 * property manager's mail look dealt with.
 *
 * ── A PAGE AT A TIME, AND THE SERVER DOES THE LOOKING ────────────────────────────────────────
 *
 * The view and the search are sent with the request, and the chips' numbers are counted there —
 * so a busy agency's older mail is found by searching or by Show older, where a list fetched once
 * and filtered here stopped at whatever the fetch stopped at. Sorting by a column orders the rows
 * that are loaded, and the line under the table says how many of the view that is.
 */
function InboxPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('mine');
  const [sort, setSort] = useState<SortState<Column>>({ column: 'received', direction: 'desc' });
  const search = useDebounced(query.trim(), 250);

  const inbox = useInfiniteQuery({
    ...inboxOptions(filter, search),
    meta: { errorTitle: 'Could not load the inbox' },
  });
  const counts = useQuery({
    ...countsOptions(),
    meta: { errorTitle: 'Could not count the inbox' },
  });
  const unreadMine = useUnreadMail();

  const rows = useMemo(
    () =>
      (inbox.data?.pages.flatMap((page) => page.messages) ?? []).sort((a, b) =>
        compareRows(a, b, sort, COLUMNS),
      ),
    [inbox.data, sort],
  );

  const count = (f: Filter) => (counts.data ? ` (${String(counts.data[f])})` : '');
  // Somebody whose reach is their own patch is only ever shown theirs and nobody's, so the widest
  // chip is honest about what it holds.
  const everything = inbox.data?.pages[0]?.reach === 'agency' ? 'Everyone’s' : 'All I can see';
  const filters: readonly { value: Filter; label: string }[] = [
    { value: 'mine', label: `Mine${count('mine')}` },
    { value: 'unassigned', label: `Unassigned${count('unassigned')}` },
    { value: 'unread', label: `Unread${count('unread')}` },
    { value: 'all', label: `${everything}${count('all')}` },
    { value: 'deleted', label: `Deleted${count('deleted')}` },
  ];
  // How many the view holds, when nothing is being searched for — the chip's own number.
  const inView = search === '' ? counts.data?.[filter] : undefined;

  return (
    <ListShell
      title="Communications"
      subtitle={
        unreadMine === undefined
          ? 'Loading…'
          : unreadMine === 0
            ? 'Nothing unread for you'
            : `${String(unreadMine)} unread for you`
      }
      nav={<CommunicationsNav current="inbox" />}
      actions={null}
      search={query}
      onSearchChange={setQuery}
      searchPlaceholder="Search sender, subject or message"
      searchLabel="Search the inbox"
      filters={
        <FilterChips
          label="Filter the inbox"
          value={filter}
          options={filters}
          onChange={setFilter}
        />
      }
      isPending={inbox.isPending}
      error={inbox.error}
      onRetry={() => void inbox.refetch()}
      isEmpty={rows.length === 0}
      empty={
        <EmptyState title={search ? `Nothing matches “${search}”` : NOTHING[filter].title}>
          {search ? 'Try a different name or subject.' : NOTHING[filter].body}
        </EmptyState>
      }
    >
      <DataTable
        rows={rows}
        columns={COLUMNS}
        sort={sort}
        onSortChange={setSort}
        rowKey={(message) => message.id}
        onOpen={(message) =>
          void navigate({ to: '/communications/inbox/$id', params: { id: message.id } })
        }
        caption={`Received email, one row per message. ${String(rows.length)} shown.`}
        minWidth="56rem"
      />
      {inbox.hasNextPage ? (
        <div className="flex flex-wrap items-center gap-3 px-5 py-3 text-[0.8125rem] text-ink-muted">
          <Button
            size="sm"
            variant="secondary"
            disabled={inbox.isFetchingNextPage}
            onClick={() => void inbox.fetchNextPage()}
          >
            {inbox.isFetchingNextPage ? 'Loading…' : 'Show older'}
          </Button>
          <span className="tabular-nums">
            The newest {rows.length}
            {inView === undefined ? '' : ` of ${String(inView)}`} shown.
          </span>
        </div>
      ) : null}
    </ListShell>
  );
}
